import { z } from 'zod';
import { CalendarDate } from './project.js';
import { Email } from './auth.js';
import { ASSIGNABLE_ORG_ROLES, OrgRole } from './auth.js';
import { Permission, ProjectRole } from './rbac.js';
/**
 * Quản trị người dùng và phân quyền — APM-402.
 *
 * ⚠️ Không schema nào ở đây chứa mật khẩu hay token. Mời người dùng nghĩa là
 * tạo tài khoản **chưa có mật khẩu** rồi cấp một link đặt mật khẩu dùng một lần
 * — dùng lại đúng cơ chế `password_reset_token` của APM-102, không sinh ra một
 * đường vào thứ hai để phải bảo vệ.
 */
export const SkillLevel = z.enum(['learning', 'proficient', 'expert']);
/**
 * Bậc nhân sự — docs/plan-project-rate-speed.md §11.
 *
 * Thứ tự trong mảng là thứ tự **từ thấp lên cao**, và giao diện dựa vào nó để
 * xếp bảng giá. Đừng sắp lại theo abc: `JUNIOR_1` < `JUNIOR_2` < `SENIOR_1` là
 * một thang, không phải một tập hợp.
 *
 * ⚠️ Khác `SkillLevel` ngay trên. `SkillLevel` là mức thành thạo một **kỹ năng**
 * do chính người ấy khai (APM-1314); `StaffLevel` là bậc trong **tổ chức** do
 * quản trị viên xếp, và nó quyết định giá thuê mặc định mỗi giờ. Hai thứ không
 * suy ra nhau.
 */
export const STAFF_LEVELS = ['JUNIOR_1', 'JUNIOR_2', 'SENIOR_1', 'SENIOR_2'];
export const StaffLevel = z.enum(STAFF_LEVELS);
export const InviteUserInput = z.object({
    email: Email,
    displayName: z.string().trim().min(1).max(200),
    orgRole: OrgRole.default('EMPLOYEE'),
    /** Quản lý trực tiếp — quyết định cột `L` của ma trận (docs/security.md §3). */
    lineManagerId: z.uuid().optional(),
});
export const InviteUserResult = z.object({
    userId: z.uuid(),
    /**
     * Link đặt mật khẩu, dùng **một lần**, hết hạn sau 1 giờ.
     *
     * Trả về ở đây vì module gửi email chưa có (APM-403 trở đi). Admin tự chuyển
     * link cho người được mời. Khi có email, trường này biến mất khỏi response —
     * một link đặt mật khẩu đi qua response body là một link nằm trong log proxy.
     */
    setPasswordUrl: z.string(),
    expiresAt: z.iso.datetime(),
});
export const AdminUserView = z.object({
    id: z.uuid(),
    email: z.string(),
    displayName: z.string(),
    /** Vị trí. Quản trị tổ chức là `isOrgAdmin`, không phải một giá trị ở đây — APM-OADM-a. */
    orgRole: OrgRole,
    isOrgAdmin: z.boolean(),
    /** Bậc nhân sự; `null` = chưa xếp bậc — docs/plan-project-rate-speed.md §11. */
    level: StaffLevel.nullable(),
    isActive: z.boolean(),
    /** `false` = đã mời nhưng chưa đặt mật khẩu lần nào. */
    hasPassword: z.boolean(),
    lineManagerId: z.uuid().nullable(),
    lineManagerName: z.string().nullable(),
    departmentId: z.uuid().nullable(),
    departmentName: z.string().nullable(),
    /** Đang bị khoá tạm vì đăng nhập sai nhiều lần (APM-102). */
    lockedUntil: z.iso.datetime().nullable(),
    /**
     * Ngày cuối làm việc — APM-1311b. `null` = vẫn đang làm.
     *
     * Khác `isActive`: một người báo trước 30 ngày vẫn `isActive = true` suốt 30
     * ngày đó, nhưng kế hoạch từ hôm sau ngày này đã tính họ bằng 0 giờ.
     */
    lastWorkingDay: CalendarDate.nullable(),
    projectCount: z.number().int().nonnegative(),
    skills: z.array(z.object({
        techStackId: z.uuid(),
        techStackName: z.string(),
        level: SkillLevel,
        yearsExperience: z.number().int().nonnegative().nullable(),
    })),
    createdAt: z.iso.datetime(),
});
/**
 * Vì sao một người chưa gỡ được khỏi tổ chức — APM-ORGRM-a.
 *
 * `last_pm` mang tên dự án: câu "là PM cuối cùng" mà không nói của dự án nào thì
 * quản trị viên phải đi mở từng dự án để tìm.
 */
export const MemberRemovalBlocker = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('self') }),
    z.object({ kind: z.literal('super_admin') }),
    z.object({ kind: z.literal('last_org_admin') }),
    z.object({ kind: z.literal('last_pm'), projectName: z.string() }),
]);
/**
 * Gỡ một người khỏi tổ chức thì sẽ ra sao — APM-ORGRM-a, đọc **trước** khi hỏi lại.
 *
 * `mode` là quyết định của người dùng (2026-10-01): người **đã có việc** (task
 * được giao trong tổ chức, mọi trạng thái) đi theo quy trình nghỉ việc — họ ở
 * lại danh sách ở trạng thái khoá, vì velocity, báo cáo hiệu suất quý trước và
 * Replay vẫn cần họ. Người **chưa có việc nào** (vào nhầm) thì gỡ hẳn ngay.
 */
export const MemberRemovalView = z.object({
    mode: z.enum(['remove', 'offboard']),
    taskCount: z.number().int().nonnegative(),
    openTaskCount: z.number().int().nonnegative(),
    projectCount: z.number().int().nonnegative(),
    /**
     * Tổ chức này có phải tổ chức gốc (`user.org_id`) của họ không. Ngày cuối làm
     * việc là cột của tài khoản và chỉ tổ chức gốc đặt được; ở tổ chức khác thì
     * đường nghỉ việc rút về hệ quả cuối của nó — khoá trong tổ chức.
     */
    homeOrg: z.boolean(),
    blocker: MemberRemovalBlocker.nullable(),
});
/**
 * Kế hoạch nghỉ việc đụng vào những gì — APM-OFFB-a. Hộp Kế hoạch nghỉ việc đọc
 * nó **trước** khi lưu, để quản trị biết ai sẽ phải xoay xở và việc gì sẽ rơi.
 *
 * Chỉ con số và tên dự án: người đọc là quản trị tổ chức (`user:manage`), vốn đã
 * thấy được danh sách dự án; tên việc cụ thể thì thuộc về PM của từng dự án.
 */
export const OffboardingImpactView = z.object({
    /** Ngày cuối đang đặt; `null` = chưa có kế hoạch. */
    lastWorkingDay: CalendarDate.nullable(),
    /** Tổ chức này có phải tổ chức gốc — chỉ tổ chức gốc đặt được ngày cuối. */
    homeOrg: z.boolean(),
    projects: z.array(z.object({
        id: z.uuid(),
        name: z.string(),
        openTasks: z.number().int().nonnegative(),
        /** Họ là PM duy nhất — dự án mất người quản lý khi họ đi. */
        isLastPm: z.boolean(),
        /** Dự án đang chạy AI bằng key cá nhân của họ (`project.ai_key_id`). */
        usesTheirAiKey: z.boolean(),
    })),
    /** Số người đang có họ làm quản lý trực tiếp. */
    directReports: z.number().int().nonnegative(),
    /** Tệp cá nhân ("Tệp của tôi") — số tệp và tổng dung lượng, kể cả bản cũ. */
    myFiles: z.object({
        count: z.number().int().nonnegative(),
        bytes: z.number().int().nonnegative(),
    }),
    /** Số dự án đang chờ họ duyệt (`project.pending_approver_id`). */
    pendingApprovals: z.number().int().nonnegative(),
    /** Ai sẽ nhận thông báo khi lưu: số PM (khác nhau) và leader (hoặc `null` ⇒ quản trị). */
    notify: z.object({
        pmCount: z.number().int().nonnegative(),
        leaderName: z.string().nullable(),
    }),
});
/**
 * Đặt hoặc bỏ ngày cuối làm việc — APM-1311b.
 *
 * `null` = huỷ nghỉ việc (người đó ở lại). Không dùng `isActive` cho việc này:
 * khoá tài khoản là **hệ quả** của ngày đó đi qua, không phải cách khai nó.
 */
export const SetLastWorkingDayInput = z.object({
    lastWorkingDay: CalendarDate.nullable(),
});
export const UpdateUserInput = z.object({
    /**
     * **Vị trí** — chỉ ba giá trị gán được (APM-OADM-b).
     *
     * Trước đây nhận cả `OrgRole`, tức một quản trị gửi `SUPER_ADMIN` hay
     * `ORG_ADMIN` là tự phong được — giao diện chỉ bày ba lựa chọn, còn API thì
     * không chặn. Quản trị tổ chức là **cờ** riêng, không đi qua trường này, nên
     * đổi vị trí của một quản trị không bao giờ đụng tới quyền quản trị của họ.
     */
    orgRole: z.enum(ASSIGNABLE_ORG_ROLES).optional(),
    /**
     * Bậc nhân sự; `null` = gỡ bậc — docs/plan-project-rate-speed.md §11.
     *
     * Ở chung `PATCH users/:id` với vai trò và phòng ban vì cùng bản chất: một
     * **thuộc tính** của tư cách thành viên, đổi xong không kéo theo hệ quả nào
     * ngoài chính nó. (So với ngày cuối làm việc, vốn có endpoint riêng vì nó
     * khoá tài khoản và thu hồi phiên.)
     *
     * Xếp bậc cho **bất kỳ ai trong tổ chức**, kể cả `LEADER` và `DIRECTOR`: bậc
     * là thang nhân sự, không phải thang quyền.
     */
    level: StaffLevel.nullable().optional(),
    /** `null` = gỡ quản lý trực tiếp. */
    lineManagerId: z.uuid().nullable().optional(),
    /** `null` = gỡ khỏi phòng ban — APM-1315. */
    departmentId: z.uuid().nullable().optional(),
    isActive: z.boolean().optional(),
});
export const TechStackView = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    isActive: z.boolean(),
    /** Bao nhiêu người khai kỹ năng này — để admin biết có nên tắt hay không. */
    userCount: z.number().int().nonnegative(),
});
export const TechStackInput = z.object({
    name: z.string().trim().min(1).max(60),
});
/**
 * Phòng ban — APM-1315.
 *
 * Cùng hình dạng với `TechStackView` có chủ đích: cả hai là **danh mục cấp tổ
 * chức** do admin quản lý, tắt chứ không xoá, và đếm số người đang dùng để admin
 * biết tắt có an toàn không. Hai màn hình giống nhau thì học một lần dùng cả hai.
 */
export const DepartmentView = z.object({
    id: z.uuid(),
    name: z.string(),
    slug: z.string(),
    isActive: z.boolean(),
    /** Bao nhiêu người đang thuộc phòng này — để admin biết có nên tắt hay không. */
    userCount: z.number().int().nonnegative(),
});
export const DepartmentInput = z.object({
    name: z.string().trim().min(1).max(80),
});
export const SetUserSkillInput = z.object({
    techStackId: z.uuid(),
    /** `null` = gỡ kỹ năng này khỏi người dùng. */
    level: SkillLevel.nullable(),
    yearsExperience: z.number().int().min(0).max(60).optional(),
});
export const ProjectMemberView = z.object({
    membershipId: z.uuid(),
    projectId: z.uuid(),
    userId: z.uuid(),
    displayName: z.string(),
    email: z.string(),
    /** `null` = vai trò **tuỳ biến** của dự án đó — APM-ROLE-b. */
    role: ProjectRole.nullable(),
    /** Tên hiển thị của vai trò. Vai trò tuỳ biến không có khoá i18n để tra. */
    roleName: z.string(),
    /**
     * Phòng ban, để phân biệt hai người trùng tên — APM-1315.
     *
     * `null` khi chưa gán. Giao diện **không** hiện dấu phân cách khi thiếu: một
     * dấu `·` treo lửng ở mọi người chưa có phòng còn tệ hơn không hiện gì.
     */
    departmentName: z.string().nullable(),
});
export const SetProjectMemberInput = z.object({
    userId: z.uuid(),
    /** `null` = gỡ khỏi project. */
    role: ProjectRole.nullable(),
});
/**
 * Ma trận phân quyền, chỉ đọc — docs/security.md §3.
 *
 * Sinh từ **chính** bảng tra mà backend dùng để chặn, không phải một bản chép
 * tay ở frontend. Admin đang gán vai trò cho người thật; họ phải thấy đúng thứ
 * hệ thống sẽ cưỡng chế, không phải thứ tài liệu nói.
 */
export const PermissionMatrixCell = z.enum([
    'yes',
    'no',
    'project_member',
    'own',
    'direct_report',
    /** Toàn bộ nhánh dưới mình, nhiều cấp — APM-PERF2-d. */
    'report_chain',
]);
export const PermissionMatrixView = z.object({
    orgRoles: z.array(OrgRole),
    projectRoles: z.array(ProjectRole),
    rows: z.array(z.object({
        permission: Permission,
        byOrgRole: z.record(z.string(), PermissionMatrixCell),
        byProjectRole: z.record(z.string(), PermissionMatrixCell),
    })),
});
/**
 * Kỹ năng của **chính tôi**, kèm danh mục để chọn — APM-1316.
 *
 * ## Vì sao trả kèm danh mục thay vì để client gọi thêm một endpoint
 *
 * `/admin/tech-stacks` đứng sau `user:manage`, mà trang Cài đặt thì mọi người
 * đăng nhập đều vào được. Một endpoint danh mục riêng không-quyền là một đường
 * nữa phải canh; gộp vào đây thì phạm vi rõ ràng — bạn xem được danh mục vì
 * bạn đang khai kỹ năng của mình.
 *
 * Danh mục chỉ gồm mục **đang bật**, cộng thêm những mục người này đã khai kể
 * cả khi chúng đã tắt: bỏ ra thì kỹ năng đã khai hiện lên không tên.
 */
export const MySkillsView = z.object({
    skills: z.array(z.object({
        techStackId: z.uuid(),
        techStackName: z.string(),
        level: SkillLevel,
        yearsExperience: z.number().int().nonnegative().nullable(),
    })),
    catalogue: z.array(z.object({ id: z.uuid(), name: z.string() })),
});
//# sourceMappingURL=admin-users.js.map